import { PDFSettingStep, PDFSettingStepOption, PDFSize } from '@core/enums/project';
import { FormattedNumberConfig, PDFSettingStepField, Field, Template } from '@core/types/models';

const CURRENCY_GB = '£[[x]]'; // GB Pound
const CURRENCY_DK_NO = '[[x]],-'; // Danish/Norwegian Krone
const CURRENCY_SE = '[[x]]:-'; // Swedish Krona
const CURRENCY_EU = '€[[x]]'; // Standard Euro - symbol before
const CURRENCY_EU_BEFORE_SPACE = '€ [[x]]'; // Euro  - symbol before
const CURRENCY_EU_AFTER_SPACE = '[[x]] €'; // Euro - symbol after with space
const CURRENCY_EU_AFTER = '[[x]]€'; // Euro - symbol after with no space
const CURRENCY_CHF = '[[x]] CHF'; // Swiss Franc
const CURRENCY_CZK = '[[x]] Kč'; // Czech Koruna
const CURRENCY_HUF = '[[x]] FT'; // Hungarian Forint
const CURRENCY_PLN = '[[x]] zł'; // Polish Zloty
const CURRENCY_RON = '[[x]] lei'; // Romanian Leu

export const currencies: FormattedNumberConfig[] = [
  { key: 'GB', value: CURRENCY_GB, thousandSeparator: ',', decimalSymbol: '.', isCurrency: true },
  { key: 'DK', value: CURRENCY_DK_NO, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'FI', value: CURRENCY_EU, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'SE', value: CURRENCY_SE, thousandSeparator: ' ', decimalSymbol: '.', isCurrency: true },
  { key: 'NO', value: CURRENCY_DK_NO, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'EE', value: CURRENCY_EU, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'LV', value: CURRENCY_EU, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'LT', value: CURRENCY_EU, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'BG', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: ' ', decimalSymbol: '.', isCurrency: true },
  { key: 'CZ', value: CURRENCY_CZK, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'BE', value: CURRENCY_EU_BEFORE_SPACE, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'NL', value: CURRENCY_EU_BEFORE_SPACE, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'FR', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: '', decimalSymbol: ',', isCurrency: true },
  { key: 'LU', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'CH', value: CURRENCY_CHF, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'AT', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'DE', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'HU', value: CURRENCY_HUF, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'IT', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'PL', value: CURRENCY_PLN, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'PT', value: CURRENCY_EU_AFTER, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'RO', value: CURRENCY_RON, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'SK', value: CURRENCY_EU_AFTER_SPACE, thousandSeparator: ' ', decimalSymbol: ',', isCurrency: true },
  { key: 'SI', value: CURRENCY_EU_AFTER, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
  { key: 'ES', value: CURRENCY_EU_AFTER, thousandSeparator: '.', decimalSymbol: ',', isCurrency: true },
];

export const percentage = [{ key: '%', value: '[[x]]%', isCurrency: false }];

export const PDFSettingStepOptions: PDFSettingStepOption[] = [
  {
    id: PDFSettingStep.DOWNLOAD_DETAILS,
    title: 'Download Details',
    longTitle: 'Download Details (Optional)',
    printTitle: 'Order Details',
    longPrintTitle: 'Order Details',
    description: `Provide additional information for your download request below. This information will be supplied along with your fact tags. Skip this step if you do not require a front sheet on your download.`,
    printDescription:
      'Provide additional information for your print request below. This information will be supplied along with your fact tags.',
  },
  {
    id: PDFSettingStep.FORMAT,
    title: 'Choose Format',
    longTitle: 'Choose your format',
    description: `Select which format you'd like your fact tags to be generated in.`,
  },
  {
    id: PDFSettingStep.CONTENT,
    title: 'Content',
    description: `Input further information to customize the flyer.`,
  },
  {
    id: PDFSettingStep.ORDER,
    title: 'Order Products',
  },
  {
    id: PDFSettingStep.PRODUCTS,
    title: 'Products',
    longTitle: 'Product Details',
  },
  {
    id: PDFSettingStep.CREATE,
    title: 'Create',
    longTitle: 'Disclaimer',
    description: 'ftg.generatepdf.disclaimer',
    flyerDescription: 'ftg.generatepdf.disclaimer.flyer',
  },
];

const fields: PDFSettingStepField[] = [
  {
    label: 'Select a Language',
    step: PDFSettingStep.FORMAT,
    name: 'cultureCode',
    type: 'select',
    options: 'cultureCode',
    required: true,
  },
  {
    label: 'Select a Template',
    step: PDFSettingStep.FORMAT,
    name: 'template',
    type: 'select',
    options: 'template',
    required: true,
  },
  {
    label: 'Export with Milwaukee® Logo',
    step: PDFSettingStep.FORMAT,
    name: 'showMilwaukeeLogo',
    type: 'boolean',
    value: true,
  },
  {
    label: 'Title',
    step: PDFSettingStep.CONTENT,
    name: 'title',
    type: 'string',
    required: true,
  },
  {
    label: 'Subtitle',
    step: PDFSettingStep.CONTENT,
    name: 'subtitle',
    type: 'string',
  },
  {
    label: 'Title Image',
    step: PDFSettingStep.CONTENT,
    name: 'titleImage',
    type: 'select',
    required: true,
    options: [
      { key: 'general', value: 'General brand' },
      { key: 'm12', value: 'M12™' },
      { key: 'm18', value: 'M18™' },
      { key: 'mx-fuel', value: 'MX FUEL™' },
      { key: 'ope', value: 'OPE' },
      { key: 'lighting', value: 'Lighting' },
      { key: 'storage', value: 'Storage' },
      { key: 'hand-tools', value: 'Hand Tools' },
      { key: 'accessories', value: 'Accessories' },
      { key: 'ppe', value: 'PPE' },
      { key: 'automotive', value: 'Automotive' },
      { key: 'carpentry', value: 'Carpentry' },
      { key: 'electrician', value: 'Electrician' },
      { key: 'plumbing', value: 'Plumbing' },
      { key: 'utility', value: 'Utility' },
      // { key: 'upload', value: 'Upload Custom Image' },
    ],
  },
  {
    label: 'Upload Title Image',
    step: PDFSettingStep.CONTENT,
    name: 'titleImageUpload',
    type: 'file',
    required: true, // Handled in the component
  },
  {
    label: 'Dealer Logo',
    step: PDFSettingStep.CONTENT,
    name: 'dealerLogo',
    type: 'file',
  },
  {
    label: 'Dealer Address',
    step: PDFSettingStep.CONTENT,
    name: 'dealerAddress',
    type: 'string',
  },
  {
    label: 'Disclaimer Text',
    description: 'For example: All product information, images and prices are subject to change without notice. Limited time offer.',
    step: PDFSettingStep.CONTENT,
    name: 'disclaimer',
    type: 'string',
    multiLine: true,
    required: true,
  },
  {
    label: 'Show Authorised Distributor Logo',
    step: PDFSettingStep.CONTENT,
    name: 'showAuthDistLogo',
    type: 'boolean',
    value: true,
  },
  {
    label: 'Prices include VAT',
    step: PDFSettingStep.CONTENT,
    name: 'includesVat',
    type: 'boolean',
  },
  {
    label: 'Order Products',
    step: PDFSettingStep.ORDER,
    name: 'orderProducts',
    type: 'orderProducts',
    required: true, // Handled in the component
  },
  {
    label: 'Products',
    step: PDFSettingStep.PRODUCTS,
    name: 'products',
    type: 'products',
    required: true, // Handled in the component
  },
  {
    label: 'Placeholder',
    step: PDFSettingStep.CREATE,
    name: 'placeholder',
    type: 'placeholder', // Require filed in step to display the step
  },
];

export const allOrderDetailFields: Field[] = [
  {
    label: 'Order Number',
    name: 'orderNumber',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'TTi Requester Email Address',
    name: 'orderEmailAddress',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Company Name',
    name: 'orderCompanyName',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Recipient Telephone',
    name: 'orderRecipientNumber',
    type: 'string',
    layout: 'halfWidth',
  },
  {
    label: 'First Name',
    name: 'orderFirstName',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Last Name',
    name: 'orderLastName',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Address Line 1',
    name: 'orderAddressLine1',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Address Line 2',
    name: 'orderAddressLine2',
    type: 'string',
    layout: 'halfWidth',
  },
  {
    label: 'City',
    name: 'orderCity',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Post Code',
    name: 'orderPostcode',
    type: 'string',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Invoiced Country',
    name: 'invoicedCountry',
    type: 'select',
    options: 'invoiceCountries',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Country',
    name: 'orderCountry',
    type: 'select',
    options: 'countries',
    required: true,
    layout: 'halfWidth',
  },
  {
    label: 'Notes',
    name: 'orderNotes',
    type: 'string',
    multiLine: true,
    layout: 'halfWidth',
  },
];

export const downloadOrderDetailFields = allOrderDetailFields
  .filter((x) => !['orderEmailAddress', 'invoicedCountry'].includes(x.name))
  .map((x) => {
    return {
      ...x,
      required: false,
    };
  });

const productFields: Field[] = [
  {
    label: 'Standard Price',
    name: 'standardPrice',
    type: 'formattedNumber',
    options: currencies,
    layout: 'halfWidth',
  },
  {
    label: 'Campaign Price',
    name: 'campaignPrice',
    type: 'formattedNumber',
    options: currencies,
    layout: 'halfWidth',
  },
  {
    label: 'Percentage Price Difference',
    name: 'percentagePriceDifference',
    type: 'formattedNumber',
    options: percentage,
    layout: 'halfWidth',
  },
  {
    label: 'Override Product Image (Optional)',
    name: 'image',
    type: 'file',
  },
  {
    label: 'Article Number Override (PIM data will otherwise be used)',
    name: 'articleNumber',
    type: 'string',
  },
  {
    label: 'Dealer article Number',
    name: 'dealerArticleNumber',
    type: 'string',
  },
];

export const PDFTagPrintTemplateOptions: Template[] = [
  {
    key: PDFSize.Print.toString(),
    value: 'Print',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', /* 'template',*/ 'showMilwaukeeLogo'].includes(x.name)),
  },
];

export const PDFTagTemplateOptions: Template[] = [
  {
    key: PDFSize.A4.toString(), // 149x69.aspx
    value: 'A4 (4 x <strong>14.9 x 6.9cm)</strong>',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.ReorderTags.toString(), // ReorderTags.aspx
    value: 'Reorder Tags',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.ReorderTagsTiled.toString(), // ReorderTagsTiled.aspx
    value: 'Reorder Tags (Tiled)',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.Flyer.toString(), // Flyer.aspx
    value: 'A4 Flyer',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.JSSA4.toString(), // jssa4.aspx
    value: 'JSS A4',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.JSSA4Double.toString(), // jssa4double.aspx
    value: 'JSS A4 (Double)',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.JSSA4Triple.toString(), // jssa4triple.aspx
    value: 'JSS A4 (Triple)',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template', 'showMilwaukeeLogo'].includes(x.name)),
  },
  {
    key: PDFSize.JSS16x9.toString(), // jss16x9.aspx
    value: 'JSS 16x9',
    products: 0,
    fields: fields.filter((x) => ['cultureCode', 'template'].includes(x.name)),
  },
];

export const PDFFlyerTemplateOptions: Template[] = [
  {
    key: '1-a4',
    value: 'A4 Flyer 1 Product',
    products: 1,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893875743056355.pdf',
  },
  {
    key: '2-a4',
    value: 'A4 Flyer 2 Products',
    products: 2,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893957085380561.pdf',
  },
  {
    key: '3-a4',
    value: 'A4 Flyer 3 Products',
    products: 3,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893880133360802.pdf',
  },
  {
    key: '4-a4',
    value: 'A4 Flyer 4 Products',
    products: 4,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893881198688676.pdf',
  },
  {
    key: '5-a4',
    value: 'A4 Flyer 5 Products',
    products: 5,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893882609492315.pdf',
  },
  {
    key: '6-a4',
    value: 'A4 Flyer 6 Products',
    products: 6,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893883947982239.pdf',
  },
  {
    key: '7-a4',
    value: 'A4 Flyer 7 Products',
    products: 7,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893890247831158.pdf',
  },
  {
    key: '8-a4',
    value: 'A4 Flyer 8 Products',
    products: 8,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893890897262487.pdf',
  },
  {
    key: '9-a4',
    value: 'A4 Flyer 9 Products',
    products: 9,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893892392048768.pdf',
  },
  {
    key: '10-a4',
    value: 'A4 Flyer 10 Products',
    products: 10,

    fields: fields.filter((x) => !['showMilwaukeeLogo'].includes(x.name)),
    productFields: productFields,
    pdf: 'https://tti-fact-tags.s3-eu-west-2.amazonaws.com/638893957646211971.pdf',
  },
];
